import React, { ReactNode, useEffect, useMemo } from 'react';
import { Layout, Menu, theme, Button, App as AntdApp, Popconfirm } from 'antd';
import type { MenuProps } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  UserOutlined,
  LogoutOutlined,
  TeamOutlined,
  SafetyOutlined,
  ClearOutlined,
  BellOutlined,
  PhoneOutlined,
  CompassOutlined,
} from '@ant-design/icons';
import granthaIcon from '../icons/Grantha.png';
import contributorsIcon from '../icons/contributors.png';
import languageIcon from '../icons/language.png';
import countryIcon from '../icons/country.png';
import hierarchyIcon from '../icons/hierarchy.png';
import vyakhyanuvadIcon from '../icons/vyakhyanuvad.png';
import savedProfileIcon from '../icons/savedprofile.png';
import purohitIcon from '../icons/purohit.png';
import vyakhyanuvadViewerIcon from '../icons/vyakhyanuvadviewer.png';
import granthaViewIcon from '../icons/granthaview.png';
import anukramanikaIcon from '../icons/anukramanika.png';
import bidhiIcon from '../icons/vidhi.png';
import karmakandaIcon from '../icons/karmakanda.png';
import sankalpaIcon from '../icons/sankalpa.png';
import pujaIcon from '../icons/puja.png';
import samagriIcon from '../icons/samagri.png';
import gotraIcon from '../icons/gotra.png';
import vedsakhaIcon from '../icons/vedsakha.png';
import relationIcon from '../icons/relation.png';
import { getCookie, setCookie } from '../utils/cookies';
import { API_URL } from '../config/apiUrls';
import { disconnectPresence, ensurePresenceConnected, usePresence } from '../hooks/usePresence';
import {
  ensureIncomingCallListeners,
  resetIncomingCallRing,
  useIncomingCallRing,
} from '../hooks/useIncomingCallRing';
import IncomingCallOverlay from '../components/call/IncomingCallOverlay';
import './MainLayout.css';

const { Header, Content, Sider } = Layout;

interface MainLayoutProps {
  children: ReactNode;
}

const MenuIcon: React.FC<{ src: string; alt: string; collapsed?: boolean }> = ({
  src,
  alt,
  collapsed = false,
}) => (
  <img
    src={src}
    alt={alt}
    style={{
      width: collapsed ? 16 : 14,
      height: collapsed ? 16 : 14,
      objectFit: 'contain',
      display: 'inline-block',
      verticalAlign: 'middle',
    }}
  />
);

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { message } = AntdApp.useApp();
  const [collapsed, setCollapsed] = React.useState(() => {
    const saved = getCookie('sidebar-collapsed');
    return saved === 'true';
  });
  const [isAuthenticated, setIsAuthenticated] = React.useState(
    () => Boolean(localStorage.getItem('token')),
  );
  const [username, setUsername] = React.useState<string>('');
  const [clearingCache, setClearingCache] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    resetIncomingCallRing();
    disconnectPresence();
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/cache/reset`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let errorMessage = `Failed to clear cache (${response.status})`;
        try {
          const data = await response.json();
          if (Array.isArray(data?.message)) {
            errorMessage = data.message.join(', ');
          } else if (typeof data?.message === 'string') {
            errorMessage = data.message;
          }
        } catch {
          // ignore JSON parse errors
        }
        message.error(errorMessage);
        return;
      }

      message.success('Cache cleared successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to clear cache';
      message.error(errorMessage);
    } finally {
      setClearingCache(false);
    }
  };

  useEffect(() => {
    setCookie('sidebar-collapsed', collapsed.toString());
  }, [collapsed]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(Boolean(token));
    if (!token) {
      setUsername('');
      resetIncomingCallRing();
      disconnectPresence();
      return;
    }
    ensurePresenceConnected(token);
    ensureIncomingCallListeners();
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUsername(payload.username || 'User');
    } catch {
      setUsername('User');
    }
  }, [location.pathname]);

  // Keep /signaling connected while authenticated so accepted peers see us as active.
  usePresence();
  useIncomingCallRing();
  
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(String(key));
  };

  const baseMenuItems = useMemo<MenuProps['items']>(
    () => [
      {
        key: '/',
        icon: <MenuIcon src={granthaIcon} alt="Grantha" collapsed={collapsed} />,
        label: 'Grantha',
      },
      {
        key: '/contributors',
        icon: <MenuIcon src={contributorsIcon} alt="Contributors" collapsed={collapsed} />,
        label: 'Contributors',
      },
      {
        key: '/languages',
        icon: <MenuIcon src={languageIcon} alt="Language" collapsed={collapsed} />,
        label: 'Language',
      },
      {
        key: '/countries',
        icon: <MenuIcon src={countryIcon} alt="Country" collapsed={collapsed} />,
        label: 'Country',
      },
      {
        key: '/hierarchy',
        icon: <MenuIcon src={hierarchyIcon} alt="Hierarchy" collapsed={collapsed} />,
        label: 'Hierarchy',
      },
      {
        key: '/vyakhyanuvad',
        icon: <MenuIcon src={vyakhyanuvadIcon} alt="Vyakhyanuvad" collapsed={collapsed} />,
        label: 'Vyakhyanuvad',
      },
      {
        key: '/users',
        icon: <TeamOutlined />,
        label: 'User Management',
      },
      {
        key: '/profile',
        icon: <UserOutlined />,
        label: 'My Profile',
      },
      {
        key: '/profile/saved',
        icon: <MenuIcon src={savedProfileIcon} alt="Saved Profiles" collapsed={collapsed} />,
        label: 'Saved Profiles',
      },
      {
        key: '/purohits',
        icon: <MenuIcon src={purohitIcon} alt="Purohits" collapsed={collapsed} />,
        label: 'Purohits',
      },
      {
        key: '/jyotish',
        icon: <CompassOutlined />,
        label: 'Jyotish',
      },
      {
        key: '/policies',
        icon: <SafetyOutlined />,
        label: 'Policies',
      },
      {
        key: '/notifications',
        icon: <BellOutlined />,
        label: 'Notifications',
      },
      {
        key: '/call',
        icon: <PhoneOutlined />,
        label: 'Call',
      },
      {
        key: '/connections',
        icon: <TeamOutlined />,
        label: 'Connections',
      },
      {
        key: '/vyakhyanuvad-viewer',
        icon: (
          <MenuIcon src={vyakhyanuvadViewerIcon} alt="Vyakhyanuvad Viewer" collapsed={collapsed} />
        ),
        label: 'Vyakhyanuvad Viewer',
      },
      {
        key: '/grantha-viewer-new',
        icon: <MenuIcon src={granthaViewIcon} alt="Grantha Viewer New" collapsed={collapsed} />,
        label: 'Grantha Viewer New',
      },
      {
        key: '/bidhi-viewer-new',
        icon: <MenuIcon src={bidhiIcon} alt="Bidhi Viewer New" collapsed={collapsed} />,
        label: 'Bidhi Viewer New',
      },
      {
        key: '/anukramanika',
        icon: <MenuIcon src={anukramanikaIcon} alt="Anukramanika" collapsed={collapsed} />,
        label: 'Anukramanika',
      },
      {
        key: '/bidhi',
        icon: <MenuIcon src={bidhiIcon} alt="Bidhi" collapsed={collapsed} />,
        label: 'Bidhi',
      },
      {
        key: '/karmakanda',
        icon: <MenuIcon src={karmakandaIcon} alt="Karmakanda" collapsed={collapsed} />,
        label: 'Karmakanda',
      },
      {
        key: '/sankalpa',
        icon: <MenuIcon src={sankalpaIcon} alt="Sankalpa" collapsed={collapsed} />,
        label: 'Sankalpa',
      },
      {
        key: '/puja',
        icon: <MenuIcon src={pujaIcon} alt="Puja" collapsed={collapsed} />,
        label: 'Puja',
      },
      {
        key: '/samagri',
        icon: <MenuIcon src={samagriIcon} alt="Samagri" collapsed={collapsed} />,
        label: 'Samagri',
      },
      {
        key: '/gotra',
        icon: <MenuIcon src={gotraIcon} alt="Gotra" collapsed={collapsed} />,
        label: 'Gotra',
      },
      {
        key: '/vedsakhas',
        icon: <MenuIcon src={vedsakhaIcon} alt="Vedsakha" collapsed={collapsed} />,
        label: 'Vedsakha',
      },
      {
        key: '/relations',
        icon: <MenuIcon src={relationIcon} alt="Relations" collapsed={collapsed} />,
        label: 'Relations',
      },
    ],
    [collapsed],
  );

  const menuItems = useMemo<MenuProps['items']>(
    () =>
      (baseMenuItems ?? []).map((item) => {
        if (!item || typeof item !== 'object' || !('label' in item)) return item;
        const label = item.label;
        if (!collapsed) return item;
        return {
          ...item,
          title: typeof label === 'string' ? label : undefined,
          label: undefined,
        };
      }),
    [baseMenuItems, collapsed],
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
        }}
      >
        <h1 style={{ color: 'white', margin: 0 }}>Grantha Management System</h1>
        {isAuthenticated ? (
          <Link to="/profile" style={{ color: 'white' }}>
            {username}
          </Link>
        ) : (
          <Link to="/login" style={{ color: 'white' }}>
            Log in
          </Link>
        )}
      </Header>
      <Layout>
        {isAuthenticated && (
        <Sider
          className="main-layout-sider"
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={200}
          collapsedWidth={64}
          style={{
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            height: '100vh',
            overflow: 'hidden',
            zIndex: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              paddingBottom: 48,
            }}
          >
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Menu
                className="main-layout-menu"
                theme="dark"
                mode="inline"
                inlineCollapsed={collapsed}
                onClick={handleMenuClick}
                selectedKeys={[
                  location.pathname.startsWith('/profile/saved')
                    ? '/profile/saved'
                    : location.pathname.startsWith('/purohits')
                      ? '/purohits'
                      : location.pathname.startsWith('/jyotish')
                        ? '/jyotish'
                        : location.pathname,
                ]}
                style={{ borderInlineEnd: 'none' }}
                items={menuItems}
              />
            </div>
            <div
              style={{
                flexShrink: 0,
                padding: collapsed ? '8px 0' : '8px 16px',
                borderTop: '1px solid #434343',
                backgroundColor: '#001529',
              }}
            >
              <Popconfirm
                title="Clear cache?"
                description="This will reset the server cache. Continue?"
                onConfirm={handleClearCache}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  icon={<ClearOutlined />}
                  loading={clearingCache}
                  style={{ color: 'white', width: '100%', marginBottom: '8px' }}
                  block
                >
                  {!collapsed && 'Clear Cache'}
                </Button>
              </Popconfirm>
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                style={{ color: 'white', width: '100%' }}
                block
              >
                {!collapsed && 'Logout'}
              </Button>
            </div>
          </div>
        </Sider>
        )}
        <Layout>
          <Content
            style={{
              margin: 0,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
              minHeight: 280,
            }}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
      {isAuthenticated ? <IncomingCallOverlay /> : null}
    </Layout>
  );
};

export default MainLayout;